/**
 * Matter global script. Deliberately tiny: everything interactive is a
 * custom element loaded by the section that needs it. This file only owns
 * behaviour that every page shares.
 */
(() => {
  const root = document.documentElement;
  const STAGGER = 65;
  const MAX_DELAY = 520;

  /* Scroll reveal choreography (see assets/critical.css [data-reveal]).
   * Siblings inside [data-reveal-group] are staggered by 65ms; an explicit
   * data-reveal-delay wins. Disabled when the merchant turns reveals off or
   * the user prefers reduced motion (both collapse --ds-motion-reveal to 0). */
  const revealEnabled =
    getComputedStyle(root).getPropertyValue('--ds-motion-reveal').trim() === '1' &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
    'IntersectionObserver' in window;

  const stagger = (scope) => {
    scope.querySelectorAll('[data-reveal-group]').forEach((group) => {
      Array.from(group.children).forEach((child, index) => {
        const target = child.hasAttribute('data-reveal') ? child : child.querySelector(':scope > [data-reveal]');
        if (!target || target.dataset.revealDelay) return;
        target.dataset.revealDelay = String(Math.min(index * STAGGER, MAX_DELAY));
      });
    });
  };

  const show = (el) => el.classList.add('is-visible');

  const reveal = (el) => {
    const delay = Math.min(Number(el.dataset.revealDelay || 0), MAX_DELAY);
    if (delay) el.style.setProperty('--reveal-delay', `${delay}ms`);
    show(el);
  };

  const observe = (scope) => {
    stagger(scope);
    const targets = scope.querySelectorAll('[data-reveal]:not(.is-visible)');
    if (!revealEnabled) {
      targets.forEach(show);
      return;
    }
    /* A mask reveal is fully clipped while hidden (clip-path inset 102%), and
     * Chrome intersects the clipped box, so such a target never reports as
     * intersecting. Observe its parent instead and map back. */
    const anchors = new Map();
    targets.forEach((el) => {
      const anchor = el.dataset.reveal === 'mask' ? el.parentElement || el : el;
      if (!anchors.has(anchor)) anchors.set(anchor, []);
      anchors.get(anchor).push(el);
    });
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          (anchors.get(entry.target) || []).forEach(reveal);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
    );
    anchors.forEach((_, anchor) => observer.observe(anchor));
  };

  observe(document);

  /* Card "save" hearts: pressed state lives in localStorage (no account needed). */
  const SAVED_KEY = 'matter:saved';
  const readSaved = () => {
    try { return JSON.parse(localStorage.getItem(SAVED_KEY) || '[]'); } catch { return []; }
  };
  const syncSaved = (scope) => {
    const saved = readSaved();
    scope.querySelectorAll('[data-card-save]').forEach((button) => {
      button.setAttribute('aria-pressed', String(saved.includes(button.dataset.cardSave)));
    });
  };
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-card-save]');
    if (!button) return;
    event.preventDefault();
    const id = button.dataset.cardSave;
    let saved = readSaved();
    saved = saved.includes(id) ? saved.filter((item) => item !== id) : saved.concat(id);
    try { localStorage.setItem(SAVED_KEY, JSON.stringify(saved)); } catch { /* private mode */ }
    document.querySelectorAll(`[data-card-save="${id}"]`).forEach((el) => el.setAttribute('aria-pressed', String(saved.includes(id))));
  });
  syncSaved(document);

  /* Card swatches: pressing a colour swaps the card image to that variant's
   * image (fetched first, then faded in), points the card links at the
   * variant and updates the quick-add form. */
  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('[data-card-swatch]');
    if (!swatch) return;
    event.preventDefault();
    const card = swatch.closest('[data-product-card]');
    if (!card) return;
    card.querySelectorAll('[data-card-swatch]').forEach((el) => el.setAttribute('aria-pressed', String(el === swatch)));
    const { image, srcset, url, variantId } = swatch.dataset;
    const primary = card.querySelector('.product-card__image--primary');
    if (primary && image && primary.dataset.current !== image) {
      primary.dataset.current = image;
      const secondary = card.querySelector('.product-card__image--secondary');
      if (secondary) secondary.hidden = true;
      const next = new Image();
      next.sizes = primary.sizes || '';
      next.onload = () => {
        if (primary.dataset.current !== image) return;
        primary.classList.add('is-swapping');
        window.setTimeout(() => {
          primary.srcset = srcset || '';
          primary.src = image;
          primary.classList.remove('is-swapping');
        }, 120);
      };
      if (srcset) next.srcset = srcset;
      next.src = image;
    }
    if (url) {
      card.querySelectorAll('.product-card__link, .product-card__title a, a.product-card__add').forEach((link) => (link.href = url));
      /* The variant chooser sheet and quick view open on the chosen colour. */
      card.querySelectorAll('[data-quick-view]').forEach((button) => (button.dataset.quickView = url));
    }
    if (variantId) {
      const input = card.querySelector('.product-card__form input[name="id"]');
      if (input) input.value = variantId;
      const add = card.querySelector('[data-cart-add]');
      if (add) add.dataset.variantId = variantId;
    }
  });

  /* <read-more data-max-height="rem">: collapses [data-read-more-body] when it
   * is taller than the limit and toggles it with [data-read-more-toggle]
   * (labels in data-more / data-less). Used by the product description and
   * the collection intro. */
  if (!customElements.get('read-more')) {
    customElements.define(
      'read-more',
      class extends HTMLElement {
        connectedCallback() {
          this.body = this.querySelector('[data-read-more-body]');
          this.button = this.querySelector('[data-read-more-toggle]');
          if (!this.body || !this.button) return;
          const max = (parseFloat(this.dataset.maxHeight) || 10) * 16;
          if (this.body.scrollHeight <= max + 48) return;
          this.style.setProperty('--read-more-max', `${max}px`);
          this.classList.add('is-collapsible');
          this.button.addEventListener('click', () => {
            const expanded = this.classList.toggle('is-expanded');
            this.button.setAttribute('aria-expanded', String(expanded));
            const label = this.button.querySelector('span');
            if (label) label.textContent = expanded ? this.button.dataset.less : this.button.dataset.more;
          });
        }
      }
    );
  }

  /* Back to top (snippets/back-to-top): shown after 1.5 screens. */
  const backToTop = document.querySelector('[data-back-to-top]');
  if (backToTop) {
    let ticking = false;
    const sync = () => {
      ticking = false;
      backToTop.hidden = window.scrollY < window.innerHeight * 1.5;
    };
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(sync);
    }, { passive: true });
    backToTop.addEventListener('click', () => {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
      document.getElementById('MainContent')?.focus({ preventScroll: true });
    });
    sync();
  }

  /* Recently viewed: product pages record their id (sections/recently-viewed). */
  const RECENT_KEY = 'matter:recent';
  const viewed = document.querySelector('[data-recently-viewed-product]');
  if (viewed && viewed.dataset.recentlyViewedProduct) {
    try {
      const id = String(viewed.dataset.recentlyViewedProduct);
      const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').filter((item) => item !== id);
      list.unshift(id);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 16)));
    } catch { /* private mode */ }
  }

  /* Quick view (snippets/quick-view-dialog + sections/quick-view): a card
   * button fetches the product's quick-view section into the global dialog.
   * component-product.js is loaded on first use. */
  const quickView = document.getElementById('QuickView');
  if (quickView) {
    const body = quickView.querySelector('[data-quick-view-body]');
    const loading = quickView.querySelector('[data-quick-view-loading]');
    let scriptReady = null;
    const ensureScript = () => {
      if (customElements.get('variant-picker')) return Promise.resolve();
      if (!scriptReady) {
        scriptReady = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = quickView.dataset.script;
          script.onload = resolve;
          script.onerror = reject;
          document.head.append(script);
        });
      }
      return scriptReady;
    };
    const reset = () => {
      body.querySelectorAll('[data-quick-view-product]').forEach((el) => el.remove());
      if (loading) loading.hidden = false;
      body.removeAttribute('aria-busy');
    };
    let requestToken = 0;
    const open = async (url, trigger) => {
      const token = ++requestToken;
      reset();
      body.setAttribute('aria-busy', 'true');
      quickView.open(trigger);
      try {
        const target = new URL(url, window.location.origin);
        const variant = target.searchParams.get('variant');
        target.search = '';
        if (variant) target.searchParams.set('variant', variant);
        target.searchParams.set('section_id', 'quick-view');
        const [html] = await Promise.all([fetch(target).then((response) => response.text()), ensureScript()]);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('[data-quick-view-product]');
        if (!next) throw new Error('quick view missing');
        /* A later request won: drop this one. */
        if (token !== requestToken) return;
        if (loading) loading.hidden = true;
        body.append(next);
        body.removeAttribute('aria-busy');
        syncSaved(next);
        next.querySelector('[data-add-button]')?.focus({ preventScroll: true });
      } catch {
        if (token === requestToken && quickView.querySelector('dialog')?.open) window.location.assign(url);
      }
    };
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-quick-view]');
      if (!trigger) return;
      event.preventDefault();
      open(trigger.dataset.quickView, trigger);
    });
    /* The cart drawer takes over once something was added. */
    document.addEventListener('drawer:open', (event) => {
      if (event.target.id === 'CartDrawer') quickView.close();
    });
    quickView.addEventListener('drawer:close', reset);
  }

  /* <custom-select> wraps a native <select> and draws the design-system
   * trigger + listbox over it. The native select stays the source of truth
   * (forms, no-JS, assistive tech through the listbox) and receives a
   * bubbling change event, so existing listeners keep working. */
  if (!customElements.get('custom-select')) {
    const CHEVRON = '<svg class="icon select__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m6 9 6 6 6-6"/></svg>';
    customElements.define(
      'custom-select',
      class extends HTMLElement {
        connectedCallback() {
          this.native = this.querySelector('select');
          if (!this.native || this.querySelector('.select__trigger')) return;
          this.id = this.id || `Select-${Math.random().toString(36).slice(2, 8)}`;
          this.trigger = document.createElement('button');
          this.trigger.type = 'button';
          this.trigger.className = 'select__trigger';
          this.trigger.setAttribute('aria-haspopup', 'listbox');
          this.trigger.setAttribute('aria-expanded', 'false');
          /* Accessible name = the field's label followed by the current value,
           * so screen readers announce both ("Sort by, Featured"). */
          this.trigger.innerHTML = `<span class="select__value" id="${this.id}-value"></span>${CHEVRON}`;
          const labelText = this.native.getAttribute('aria-label');
          let labelEl = this.native.id ? document.querySelector(`label[for="${CSS.escape(this.native.id)}"]`) : null;
          labelEl = labelEl || this.closest('label');
          if (labelEl) {
            if (!labelEl.id) labelEl.id = `${this.id}-label`;
            this.trigger.setAttribute('aria-labelledby', `${labelEl.id} ${this.id}-value`);
          } else if (labelText) {
            const hidden = document.createElement('span');
            hidden.id = `${this.id}-label`;
            hidden.className = 'visually-hidden';
            hidden.textContent = labelText;
            this.append(hidden);
            this.trigger.setAttribute('aria-labelledby', `${hidden.id} ${this.id}-value`);
          }
          this.menu = document.createElement('ul');
          this.menu.className = 'select__menu';
          this.menu.setAttribute('role', 'listbox');
          if (this.trigger.hasAttribute('aria-labelledby')) this.menu.setAttribute('aria-labelledby', this.trigger.getAttribute('aria-labelledby').split(' ')[0]);
          this.menu.id = `${this.id}-menu`;
          this.menu.tabIndex = -1;
          this.menu.hidden = true;
          this.trigger.setAttribute('aria-controls', this.menu.id);
          this.buildOptions();
          this.native.tabIndex = -1;
          this.native.setAttribute('aria-hidden', 'true');
          this.append(this.trigger, this.menu);
          this.classList.add('is-enhanced');
          this.trigger.addEventListener('click', () => (this.menu.hidden ? this.open() : this.close()));
          this.trigger.addEventListener('keydown', (event) => {
            if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
              event.preventDefault();
              this.open();
            }
          });
          this.menu.addEventListener('click', (event) => {
            const option = event.target.closest('[role="option"]');
            if (option) this.choose(Number(option.dataset.index));
          });
          this.menu.addEventListener('keydown', (event) => this.onMenuKey(event));
          document.addEventListener('click', (event) => {
            if (!this.contains(event.target)) this.close(false);
          });
          this.native.addEventListener('change', () => this.sync());
          new MutationObserver(() => this.buildOptions()).observe(this.native, { childList: true, subtree: true });
          this.sync();
        }

        buildOptions() {
          this.menu.innerHTML = '';
          Array.from(this.native.options).forEach((option, index) => {
            const item = document.createElement('li');
            item.className = 'select__option';
            item.setAttribute('role', 'option');
            item.id = `${this.menu.id}-${index}`;
            item.dataset.index = String(index);
            item.textContent = option.textContent;
            if (option.disabled) item.setAttribute('aria-disabled', 'true');
            this.menu.append(item);
          });
          this.sync();
        }

        sync() {
          const index = this.native.selectedIndex;
          const value = this.querySelector('.select__value');
          if (value) value.textContent = this.native.options[index]?.textContent || '';
          this.menu.querySelectorAll('[role="option"]').forEach((item, i) => {
            item.setAttribute('aria-selected', String(i === index));
            item.tabIndex = i === index ? 0 : -1;
          });
          this.menu.setAttribute('aria-activedescendant', `${this.menu.id}-${index}`);
        }

        open() {
          this.menu.hidden = false;
          this.trigger.setAttribute('aria-expanded', 'true');
          this.classList.add('is-open');
          const rect = this.menu.getBoundingClientRect();
          this.classList.toggle('is-above', rect.bottom > window.innerHeight - 8 && rect.top > rect.height + 8);
          (this.menu.querySelector('[aria-selected="true"]') || this.menu.firstElementChild)?.focus({ preventScroll: true });
        }

        close(refocus = true) {
          if (this.menu.hidden) return;
          this.menu.hidden = true;
          this.trigger.setAttribute('aria-expanded', 'false');
          this.classList.remove('is-open', 'is-above');
          if (refocus) this.trigger.focus({ preventScroll: true });
        }

        choose(index) {
          if (this.native.options[index]?.disabled) return;
          if (this.native.selectedIndex !== index) {
            this.native.selectedIndex = index;
            this.native.dispatchEvent(new Event('change', { bubbles: true }));
          }
          this.sync();
          this.close();
        }

        onMenuKey(event) {
          const items = Array.from(this.menu.querySelectorAll('[role="option"]'));
          const current = items.indexOf(document.activeElement);
          const focusAt = (i) => items[Math.max(0, Math.min(items.length - 1, i))]?.focus({ preventScroll: true });
          switch (event.key) {
            case 'ArrowDown': event.preventDefault(); focusAt(current + 1); break;
            case 'ArrowUp': event.preventDefault(); focusAt(current - 1); break;
            case 'Home': event.preventDefault(); focusAt(0); break;
            case 'End': event.preventDefault(); focusAt(items.length - 1); break;
            case 'Enter':
            case ' ': event.preventDefault(); if (current >= 0) this.choose(current); break;
            case 'Escape': event.preventDefault(); this.close(); break;
            case 'Tab': this.close(false); break;
            default:
              if (event.key.length === 1 && /\S/.test(event.key)) {
                const needle = event.key.toLowerCase();
                const match = items.find((item, i) => i > current && item.textContent.trim().toLowerCase().startsWith(needle)) || items.find((item) => item.textContent.trim().toLowerCase().startsWith(needle));
                match?.focus({ preventScroll: true });
              }
          }
        }
      }
    );
  }

  /* Pause/play for anything that moves on its own (snippets/motion-toggle):
   * CSS animations stop through `.is-paused` on the root, slider timers
   * listen for `motion:toggle`. */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-motion-toggle]');
    if (!toggle) return;
    const paused = toggle.getAttribute('aria-pressed') !== 'true';
    toggle.setAttribute('aria-pressed', String(paused));
    const scope = toggle.closest('[data-motion-root]');
    if (!scope) return;
    scope.classList.toggle('is-paused', paused);
    scope.dispatchEvent(new CustomEvent('motion:toggle', { detail: { paused } }));
  });

  /* Theme editor: newly added sections must not stay hidden. */
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-reveal]').forEach(show);
  });

  /* Theme editor: what the merchant selects is what the preview shows. The
   * cart drawer opens with its section, collapsible blocks (FAQ, product
   * details) open when selected. */
  document.addEventListener('shopify:section:select', (event) => {
    const drawer = event.target.querySelector?.('dialog-drawer#CartDrawer');
    if (drawer) drawer.open?.();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    const drawer = event.target.querySelector?.('dialog-drawer#CartDrawer');
    if (drawer) drawer.close?.();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const details = event.target.matches?.('details') ? event.target : event.target.querySelector?.('details') || event.target.closest?.('details');
    if (details) details.open = true;
  });

  /* Section Rendering API updates (facets, load more) re-observe new nodes. */
  document.addEventListener('matter:reveal', (event) => {
    const scope = event.target instanceof Element ? event.target : document;
    observe(scope);
    syncSaved(scope);
  });
})();
